import { createContext, useContext, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "@/contexts/auth-context";
import { useOfflineSync } from "@/hooks/shared/use-offline-sync";
import { CommandPaletteProvider } from "@/components/search/command-palette-provider";
import { SECURITY_TABS, SecurityTabBar } from "@/components/security/security-tab-bar";
import { ROUTES } from "@/routes/paths";

interface SecurityShell {
  pending: number;
  isSyncing: boolean;
  isOnline: boolean;
  sync: () => void;
  logout: () => void;
  postName: string;
}

const SecurityShellContext = createContext<SecurityShell | null>(null);

/**
 * Estado da casca da App do Segurança. `null` quando a página corre dentro do
 * dashboard normal (um admin a espreitar) — aí não há casca.
 */
export const useSecurityShell = () => useContext(SecurityShellContext);

/**
 * Casca da App do Segurança — ocupa o ecrã TODO, como uma app.
 *
 * Contextos que isto tem de aguentar, todos ao mesmo tempo:
 *
 *  • PWA instalada num iPhone com notch/Dynamic Island → o topo do ecrã está
 *    tapado. Daí `env(safe-area-inset-top)`: sem ele o título ficava por baixo
 *    do relógio.
 *  • PWA num iPhone com home indicator → a barra inferior ficaria por baixo da
 *    barrinha do sistema. Daí `env(safe-area-inset-bottom)`.
 *  • Browser (Safari/Chrome no telemóvel) → a barra de endereço encolhe ao
 *    rolar e o `100vh` MENTE (fica maior que o ecrã visível, e a barra de
 *    separadores cai para fora). `100dvh` é a única unidade que acompanha.
 *  • Ecrãs com cantos arredondados → `env(safe-area-inset-left/right)` evita
 *    que o conteúdo desapareça nos cantos em modo horizontal.
 *
 * NÃO desenha header: quem o desenha é a página, para haver UM só cabeçalho.
 * Desenha a BARRA DE BAIXO, que é o contrário: é moldura, fica parada enquanto
 * o conteúdo troca, e é ela que navega entre os separadores.
 */
export const SecurityAppLayout = ({ children }: { children: ReactNode }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const { pending, isSyncing, isOnline, sync } = useOfflineSync();

  // Trancado na app do segurança: nada de vaguear pelo resto da plataforma.
  // Os quatro separadores da barra de baixo mais as visitas, que são da portaria
  // (quem chega, quem sai) e a pesquisa global do posto devolve-as — sem isto,
  // clicar num resultado de visita era atirado de volta para o ecrã inicial.
  const ALLOWED: string[] = [...SECURITY_TABS.map((t) => t.to), ROUTES.VISITS];
  if (!ALLOWED.includes(location.pathname)) {
    return <Navigate to={ROUTES.SECURITY} replace />;
  }

  const shell: SecurityShell = {
    pending,
    isSyncing,
    isOnline,
    sync,
    logout: () => void logout(),
    postName: user?.name ?? "Posto",
  };

  return (
    <SecurityShellContext value={shell}>
      {/* O posto também pesquisa (⌘K) — só encontra o domínio `security`, que é
          o que o backend lhe devolve. Sem este provider, o segurança era o único
          utilizador da plataforma sem pesquisa nenhuma. */}
      <CommandPaletteProvider>
      <div
        className={[
          "flex h-dvh w-full flex-col overflow-hidden bg-muted/40",
          // Sem flash cinzento ao tocar (Android) e sem duplo-toque-para-zoom:
          // é o que separa "app" de "página aberta no browser".
          "[-webkit-tap-highlight-color:transparent] touch-manipulation",
          // Não deixar seleccionar texto ao manter premido — nas apps nativas
          // manter premido num botão não abre a lupa de selecção.
          "select-none",
        ].join(" ")}
        style={{
          paddingLeft: "env(safe-area-inset-left)",
          paddingRight: "env(safe-area-inset-right)",
        }}
      >
        {children}

        {/* Barra inferior — o padrão nativo: ao alcance do polegar.
            `bg-background` sólido (não translúcido): sobre o fundo cinzento da
            app, um vidro fosco a 80% deixava a barra "a flutuar" e o conteúdo
            lia-se por baixo dos ícones. */}
        {/* A partir de `lg` a barra DESAPARECE: nesse tamanho a página põe as
            secções todas lado a lado, em colunas. Um separador que troca de
            ecrã não faz sentido quando já não há nada escondido para trocar —
            e o guarda da portaria precisa de ver quem falta chegar ao mesmo
            tempo que regista uma entrada.
            Até `md` (telemóvel e tablet) mantém-se exactamente como estava. */}
        <SecurityTabBar />
      </div>
      </CommandPaletteProvider>
    </SecurityShellContext>
  );
};
